/**
 * Frontend Period Format Helper
 * Standardizes period formats across the entire frontend application
 * Single source of truth for period format conversions
 */

/** Any accepted period identifier: a bare number, a formatted string, or nullish. */
type PeriodInput = string | number | null | undefined;

/**
 * Standardizes period identifier to "Period X" format
 * @param period - Period identifier in any format
 * @return Standardized period format ("Period 1", "Advisory", etc.)
 */
export function standardizePeriodFormat(period: PeriodInput): string | null {
  if (!period) return null;

  // Convert to string for processing
  const periodStr = period.toString().trim();

  // Already in correct format
  if (periodStr.match(/^Period \d+$/)) {
    return periodStr;
  }

  // Handle special periods (case insensitive)
  const specialPeriods = ['Advisory', 'Lunch', 'Break', 'Homeroom'];
  for (const special of specialPeriods) {
    if (periodStr.toLowerCase() === special.toLowerCase()) {
      return special; // Return with proper capitalization
    }
  }

  // Normalize any bare number to "Period N" (shape only). Range validity
  // (1-20) is enforced separately by isValidPeriod, not here.
  const numMatch = periodStr.match(/^(\d+)$/);
  if (numMatch) {
    return `Period ${parseInt(periodStr, 10)}`;
  }

  // Return as-is if no pattern matches (for unknown formats)
  console.warn(`⚠️ Unknown period format: ${period}`);
  return periodStr;
}

/**
 * Extracts period number from standardized format
 * @param period - Period in "Period X" format
 * @return Period number or null if not a numbered period
 */
export function extractPeriodNumber(period: PeriodInput): number | null {
  if (!period) return null;

  const match = period.toString().match(/^Period (\d+)$/);
  return match ? parseInt(match[1]) : null;
}

/**
 * Checks if two periods are equivalent regardless of format
 * @param period1 - First period identifier
 * @param period2 - Second period identifier
 * @return True if periods are equivalent
 */
export function periodsEqual(period1: PeriodInput, period2: PeriodInput): boolean {
  const std1 = standardizePeriodFormat(period1);
  const std2 = standardizePeriodFormat(period2);
  return std1 === std2;
}

/**
 * Validates if a period format is valid
 * @param period - Period identifier
 * @return True if valid period format
 */
export function isValidPeriod(period: PeriodInput): boolean {
  if (!period) return false;

  // `period` is truthy here, so standardizePeriodFormat never returns null.
  const standardized = standardizePeriodFormat(period)!;

  // Check if it's a numbered period (1-20)
  if (standardized.match(/^Period \d+$/)) {
    const num = extractPeriodNumber(standardized);
    return num !== null && num >= 1 && num <= 20;
  }

  // Check if it's a special period
  const specialPeriods = ['Advisory', 'Lunch', 'Break', 'Homeroom'];
  return specialPeriods.includes(standardized);
}

/**
 * Migrates period data to standardized format
 * @param data - Data object containing period references
 * @param periodFields - Array of field names that contain period data
 * @return Data with standardized period formats
 */
export function migratePeriodData(
  data: unknown,
  periodFields: string[] = ['currentPeriod', 'period']
): unknown {
  if (!data || typeof data !== 'object') return data;

  const migratedData: Record<string, unknown> = {
    ...(data as Record<string, unknown>),
  };

  for (const field of periodFields) {
    if (migratedData[field]) {
      migratedData[field] = standardizePeriodFormat(
        migratedData[field] as PeriodInput
      );
    }
  }

  return migratedData;
}

/**
 * Converts a time value to minutes since midnight.
 * @param timeInput - "HH:MM" string, or already-numeric minutes
 * @return Minutes since midnight (0 if input is invalid)
 */
export function timeToMinutes(timeInput: unknown): number {
  if (typeof timeInput === 'number') {
    return timeInput;
  }

  if (typeof timeInput === 'string') {
    const [hours, minutes] = timeInput.split(':').map(Number);
    if (Number.isFinite(hours) && Number.isFinite(minutes)) {
      return hours * 60 + minutes;
    }
  }

  console.error(
    `[periodFormatHelper] timeToMinutes received invalid input:`,
    timeInput
  );
  return 0;
}

/** A period's time range within a schedule, optionally tagged to an A/B day. */
interface TimeRangePeriod {
  start: string;
  end: string;
  label: string;
  abDay?: string | null;
}

/**
 * Finds pairwise-overlapping time ranges among periods, optionally grouped by
 * A/B day for split schedules -- same pairwise-comparison + day-grouping
 * algorithm as SchoolSetupWizard.js's validateTimeOverlaps (including its
 * 'Both'-day periods must be checked against both A and B groups fix),
 * extracted here so other save flows editing a periods list (e.g.
 * ScheduleVariantModal.js) can reuse it instead of reimplementing overlap
 * detection.
 * @param periods
 * @param groupByDay - true to check A/B day groups independently
 *   (a period tagged abDay:'Both' is checked against both groups); false to
 *   check every period against every other period directly.
 * @return human-readable overlap error messages
 */
export function findTimeOverlaps(
  periods: TimeRangePeriod[],
  groupByDay: boolean
): string[] {
  const errors: string[] = [];

  const checkPairwiseOverlaps = (
    group: TimeRangePeriod[],
    dayLabel: string | null = null
  ): void => {
    for (let i = 0; i < group.length; i++) {
      for (let j = i + 1; j < group.length; j++) {
        const p1 = group[i];
        const p2 = group[j];

        if (!p1.start || !p1.end || !p2.start || !p2.end) {
          continue;
        }

        const start1 = timeToMinutes(p1.start);
        const end1 = timeToMinutes(p1.end);
        const start2 = timeToMinutes(p2.start);
        const end2 = timeToMinutes(p2.end);

        if (start1 < end2 && start2 < end1) {
          const label = dayLabel ? ` on ${dayLabel} day` : '';
          errors.push(
            `Time overlap${label}: ${p1.label} (${p1.start}-${p1.end}) and ${p2.label} (${p2.start}-${p2.end})`
          );
        }
      }
    }
  };

  if (groupByDay) {
    const dayGroups: Record<'A' | 'B', TimeRangePeriod[]> = {
      A: periods.filter((p) => p.abDay === 'A' || p.abDay === 'Both' || !p.abDay),
      B: periods.filter((p) => p.abDay === 'B' || p.abDay === 'Both' || !p.abDay),
    };

    for (const [day, dayPeriods] of Object.entries(dayGroups)) {
      if (dayPeriods.length < 2) continue;
      checkPairwiseOverlaps(dayPeriods, day);
    }
  } else if (periods.length >= 2) {
    checkPairwiseOverlaps(periods);
  }

  return errors;
}

// Explicit times for the first 4 academic periods (period 3 leaves a longer
// gap after period 2 to make room for Advisory/Lunch). Periods 5+ continue
// the same PERIOD_DURATION_MINUTES/PASSING_PERIOD_MINUTES pattern directly
// after period 4 via getDefaultStartTime/getDefaultEndTime below, instead of
// falling back to a fixed time that collides with period 1.
const EXPLICIT_START_TIMES: Record<number, string> = {
  1: '08:30',
  2: '10:00',
  3: '12:30',
  4: '14:00',
};
const EXPLICIT_END_TIMES: Record<number, string> = {
  1: '09:55',
  2: '11:25',
  3: '13:55',
  4: '15:25',
};
const PERIOD_DURATION_MINUTES = 85;
const PASSING_PERIOD_MINUTES = 5;

// <input type="time"> only accepts 00:00-23:59. Clamp so extrapolated
// periods far past period 4 (totalPeriods goes up to 20 in the setup
// wizard's UI) never overflow past midnight into an invalid value like
// '24:30' or '35:00' -- periods that clamp to 23:59 are a degenerate
// default the admin is expected to edit, not a crash/blank-field regression.
function minutesToTime(totalMinutes: number): string {
  const clamped = Math.min(Math.max(totalMinutes, 0), 1439);
  const hours = Math.floor(clamped / 60);
  const minutes = clamped % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Default start time for a numbered academic period. Periods 1-4 use
 * explicit times; periods 5+ continue sequentially after period 4's start,
 * PERIOD_DURATION_MINUTES + PASSING_PERIOD_MINUTES apart, clamped to a
 * valid <input type="time"> range.
 * @param periodNum
 * @return "HH:MM"
 */
export function getDefaultStartTime(periodNum: number): string {
  if (EXPLICIT_START_TIMES[periodNum]) return EXPLICIT_START_TIMES[periodNum];
  const offset =
    (periodNum - 4) * (PERIOD_DURATION_MINUTES + PASSING_PERIOD_MINUTES);
  return minutesToTime(timeToMinutes(EXPLICIT_START_TIMES[4]) + offset);
}

/**
 * Default end time for a numbered academic period. See getDefaultStartTime.
 * @param periodNum
 * @return "HH:MM"
 */
export function getDefaultEndTime(periodNum: number): string {
  if (EXPLICIT_END_TIMES[periodNum]) return EXPLICIT_END_TIMES[periodNum];
  return minutesToTime(
    timeToMinutes(getDefaultStartTime(periodNum)) + PERIOD_DURATION_MINUTES
  );
}
